import React, { useEffect, useMemo, useState } from "react";
import {
  Modal,
  DatePicker,
  Form,
  InputNumber,
  Input,
  Select,
  Table,
  Button,
  message,
} from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import moment from "moment";
import axios from "axios";
import dayjs from "dayjs";
const { Option } = Select;
const { TextArea } = Input;

const UpdateGenerateProposalModal = ({ visible, onOk, onCancel, proposalId }) => {
  const [form] = Form.useForm();
  const [outletItem, setItems] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [representaive, setRepesentaive] = useState([]);
  const [subTotal, setSubTotal] = useState(0);
  const [sgst, setSgst] = useState(0);
  const [cgst, setCgst] = useState(0);
  const [igst, setIgst] = useState(0);
  const [total, setTotal] = useState(0);
  const [checkState, setCheckState] = useState("");
  const [sameState, setSameState] = useState(true);
  const [initialValuesLoaded, setInitialValuesLoaded] = useState(false);
  const apiUrl = process.env.REACT_APP_API_URL;

  useEffect(() => {
    if (!visible) return;

    const fetchProfileSetting = async () => {
      try {
        const response = await axios.get(`${apiUrl}/api/setting/getCompanyDetail`);
        setCheckState(response?.data?.profile?.company_address?.state || "");
        // console.log(response, "fweetch profile setting")
      } catch (err) {
        console.error("Error fetching profile", err);
      }
    };

    const fetchAllAuditors = async () => {
      try {
        const response = await axios.get(`${apiUrl}/api/auth/getAllRepresentatives`);
        setRepesentaive(response?.data?.data || []);
      } catch (err) {
        console.error("Error fetching reps", err);
      }
    };

    const fetchProposal = async () => {
      try {
        const response = await axios.get(`${apiUrl}/api/proposal/getProposalById/${proposalId}`);
        const pd = response.data;
        // console.log("Fetche Proposal Data", pd);

        // set form fields
        form.setFieldsValue({
          fbo_name: pd.fbo_name,
          address: { line1: pd.address?.line1 || "", line2: pd.address?.line2 || "" },
          pincode: pd.pincode || "",
          gst_number: pd.gst_number || "",
          contact_person: pd.contact_person || "",
          phone: pd.phone || "",
          proposal_number: pd.proposal_number || "",
          proposal_date: pd.proposal_date ? dayjs(pd.proposal_date) : undefined,
          representative: pd.representative || undefined,
          note: pd.note || "",
          auditor_convenience_fee: pd.auditor_convenience_fee || 0,
          po_number: pd.po_number || "",
        });

        // if there's an enquiryId, fetch outlet definitions (for select options)
        if (pd.enquiryId) {
          try {
            const outletRes = await axios.get(`${apiUrl}/api/proposal/getOutletDetailsById/${pd.enquiryId}`);
            setOutlets(outletRes.data || []);
          } catch (err) {
            console.error("Error fetching outlet details", err);
          }
        }

        // Map incoming endpoints to table rows (use incoming outlet values where present)
        const mapped = (pd.outlets || []).map((o) => {
          // safely extract numeric values from mongoose wrappers if any
          const qty = o.quantity?.$numberInt ?? o.quantity ?? 0;
          const uc = o.unit_cost?.$numberInt ?? o.unit_cost ?? 0;
          const amt = o.amount?.$numberInt ?? o.amount ?? 0;
          const unit = o.unit ?? o.unit ?? 0;
          const prod = o.no_of_production_line ?? o.no_of_production_line ?? 0;

          return {
            outletId: o.outletId ?? o._id ?? "", // might be outletId or _id
            outlet_name: o.outlet_name || o.branch_name || (o.outletIdLabel || ""),
            type_of_industry: o.type_of_industry || "",
            vertical_of_industry: o.vertical_of_industry || "",
            unit: unit,
            no_of_production_line: prod,
            man_days: o.man_days ?? 0,
            quantity: Number(qty) || 0,
            unit_cost: Number(uc) || 0,
            description: o.description || "",
            amount: Number(amt) || Number(qty) * Number(uc) || 0,
            city: o.city || "",
          };
        });

        setItems(mapped.length ? mapped : [
          {
            outletId: "",
            outlet_name: "",
            type_of_industry: "",
            unit: 0,
            no_of_production_line: 0,
            man_days: 0,
            quantity: 0,
            unit_cost: 0,
            description: "",
            amount: 0,
            city: "",
          },
        ]);

        // Determine sameState using address.state if available (fallback to parsing line2)
        const stateFromAddress = pd.address?.state || (() => {
          try {
            const line2 = pd.address?.line2 || "";
            const parts = line2.split(",").map((s) => s.trim()).filter(Boolean);
            return parts.length ? parts[parts.length - 1] : "";
          } catch {
            return "";
          }
        })();
        // console.log("update genrate proposal checkState:", checkState, "and stateFromAddress:", stateFromAddress);
        const state1 = checkState?.trim().toLowerCase();
        const state2 = stateFromAddress?.trim().toLowerCase();


        // setSameState((checkState || "") === (stateFromAddress || ""));
        setSameState(state1?.replace(/\s+/g, "") === state2?.replace(/\s+/g, ""));
        // console.log("upate genrate proposal Same state value :", sameState)
        setInitialValuesLoaded(true);
      } catch (err) {
        console.error("Error fetching proposal", err);
      }
    };

    fetchProfileSetting();
    fetchAllAuditors();
    fetchProposal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, proposalId]);

  // Recalculate totals whenever items or sameState change
  useEffect(() => {
    calculateTotals(outletItem);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outletItem, sameState]);

  const calculateTotals = (items) => {
    if (!Array.isArray(items) || items.length === 0) {
      setSubTotal(0);
      setSgst(0);
      setCgst(0);
      setIgst(0);
      setTotal(0);
      return;
    }
    const calculatedSubTotal = items.reduce((sum, it) => sum + (Number(it.amount) || 0), 0);

    const calculatedSubTotalWithAuditorFee = calculatedSubTotal + (form.getFieldValue("auditor_convenience_fee") || 0);
    setSubTotal(calculatedSubTotalWithAuditorFee);
    if (sameState) {
      const part = +(calculatedSubTotalWithAuditorFee * 0.09).toFixed(2);
      setSgst(part);
      setCgst(part);
      setTotal(+(calculatedSubTotalWithAuditorFee + part + part).toFixed(2));
    } else {
      const g = +(calculatedSubTotalWithAuditorFee * 0.18).toFixed(2);
      setIgst(g);
      // setSgst(g);
      setTotal(+(calculatedSubTotalWithAuditorFee + g ).toFixed(2));
    }
  };
  const calculateHygieneRatingManDays = (foodHandlers) => {
    if (foodHandlers <= 50) return 0.5;
    if (foodHandlers <= 100) return 1;
    return 1.5;
  };

  // Man-day calculators (kept same logic)
  const calculateManDays = (foodHandlers) => {
    // if (foodHandlers <= 25) return 0.5;
    // if (foodHandlers <= 50) return 1;
    // if (foodHandlers <= 100) return 1.5;
    // return 2;
    if (foodHandlers <= 25) return 0.5;
    if (foodHandlers >= 26 && foodHandlers <= 50) return 1;
    if (foodHandlers >= 51 && foodHandlers <= 100) return 1.5;
    return 2;
  };

  const calculateStorageManDays = (area) => {
    // if (area < 15000) return 0.5;
    // if (area <= 50000) return 1;
    // return 1.5;
    if (area <= 15000) return 0.5;
    if (area > 15000 && area <= 50000) return 1;
    if (area > 50000) return 1.5;
    return 0;
  };

  const calculateVehicleManDays = (noOfVehicles) => {
    // if (noOfVehicles <= 2) return 0.5;
    // if (noOfVehicles <= 5) return 1;
    // if (noOfVehicles <= 7) return 1.5;
    if (noOfVehicles <= 2) return 0.5;
    if (noOfVehicles >= 3 && noOfVehicles <= 5) return 1;
    if (noOfVehicles = 6) return 1.5;
    if (noOfVehicles >= 7 && noOfVehicles <= 10) return 2;
    return 2;
  };

  const calculateManufacturingManDays = (foodHandlers, productionLine) => {
    let manDaysFoodHandlers = 0;
    let manDaysProductionLine = 0;
    if (foodHandlers <= 50) manDaysFoodHandlers = 0.5;
    else if (foodHandlers <= 100) manDaysFoodHandlers = 1;
    else if (foodHandlers <= 300) manDaysFoodHandlers = 1.5;
    else if (foodHandlers <= 600) manDaysFoodHandlers = 2;
    else if (foodHandlers <= 1000) manDaysFoodHandlers = 2.5;
    else manDaysFoodHandlers = 3;

    // if (!productionLine || productionLine <= 1) manDaysProductionLine = 0.5;
    // else if (productionLine <= 3) manDaysProductionLine = 1;
    // else if (productionLine <= 6) manDaysProductionLine = 1.5;
    // else manDaysProductionLine = 2;
    if (!productionLine || productionLine <= 1) {
      manDaysProductionLine = 0.5;
    } else if (productionLine <= 2) {
      manDaysProductionLine = 1;
    } else if (productionLine <= 4) {
      manDaysProductionLine = 1.5;
    } else if (productionLine <= 6) {
      manDaysProductionLine = 2;
    } else if (productionLine <= 8) {
      manDaysProductionLine = 2.5;
    } else {
      // 10+
      manDaysProductionLine = 3;
    }

    return Math.max(manDaysFoodHandlers, manDaysProductionLine);
  };

  // const calculateManDaysByIndustry = (type, unit, productionLine) => {
  //   if (type === "Catering") return calculateManDays(unit);
  //   if (type === "Transportation") return calculateVehicleManDays(unit);
  //   if (type === "Trade and Retail") return calculateStorageManDays(unit);
  //   return calculateManufacturingManDays(unit, productionLine);
  // };

  const calculateManDaysByIndustry = (
    type,
    unit,
    productionLine,
    service
  ) => {
    if (service === "Hygiene Rating") {
      return calculateHygieneRatingManDays(unit);
    }

    if (type === "Catering") return calculateManDays(unit);
    if (type === "Transportation") return calculateVehicleManDays(unit);
    if (type === "Trade and Retail" || type === "Storage and Warehouse") return calculateStorageManDays(unit);

    return calculateManufacturingManDays(unit, productionLine);
  };

  // Determine whether to show Production Line column
  const hasManufacturing = useMemo(
    () => Array.isArray(outletItem) && outletItem.some((it) => it.type_of_industry === "Manufacturing"),
    [outletItem]
  );

  // handlers
  const addItem = () => {
    setItems((prev) => [
      ...prev,
      {
        outletId: "",
        outlet_name: "",
        type_of_industry: "",
        unit: 0,
        no_of_production_line: 0,
        man_days: 0,
        quantity: 0,
        unit_cost: 0,
        description: "",
        amount: 0,
        city: "",
      },
    ]);
  };

  const removeItem = (index) => {
    const next = outletItem.filter((_, i) => i !== index);
    setItems(next);
  };

  // When user selects outlet (labelInValue option passed), populate read-only unit/no_of_production_line etc.
  const handleOutletSelect = (index, option) => {
    // option is { value, label } because we'll use labelInValue in Select
    const val = option?.value ?? option;
    const label = option?.label ?? "";
    const selected = outlets.find((o) => o._id === val) || {};
    setItems((prev) => {
      const next = [...prev];
      if (val === "others") {
        next[index] = {
          ...next[index],
          outletId: "others",
          outlet_name: "Others",
          type_of_industry: "",
          unit: 0,
          no_of_production_line: 0,
          man_days: 0,
          quantity: 0,
          unit_cost: 0,
          amount: 0,
          description: "",
        };
      } else {
        const unit = selected.unit ?? 0;
        const prod = selected.no_of_production_line ?? selected.no_of_production_line ?? 0;
        const type = selected.type_of_industry ?? "";
        // const man_days = calculateManDaysByIndustry(type, unit, prod);
        const service = next[index]?.description || "";

        const man_days = calculateManDaysByIndustry(
          type,
          unit,
          prod,
          service
        );
        next[index] = {
          ...next[index],
          outletId: selected._id || val,
          outlet_name: selected.branch_name || selected.outlet_name || label || "",
          type_of_industry: type,
          vertical_of_industry: selected.vertical_of_industry || "",
          unit,
          no_of_production_line: prod,
          man_days,
          quantity: 0,
          unit_cost: 0,
          amount: 0,
          description: "",
          city: selected.city || "",
        };
      }
      return next;
    });
  };

  const handleInputChange = (index, field, value) => {
    setItems((prev) => {
      const next = [...prev];
      const cur = { ...(next[index] || {}) };
      if (!cur) return prev;

      if (field === "quantity") {
        const q = Number(value) || 0;
        cur.quantity = q;
        cur.amount = +(q * (Number(cur.unit_cost) || 0)).toFixed(2);
      } else if (field === "unit_cost") {
        const uc = Number(value) || 0;
        cur.unit_cost = uc;
        cur.amount = +(uc * (Number(cur.quantity) || 0)).toFixed(2);
      }
      // else if (field === "description") {
      //   cur.description = value;
      // }
      else if (field === "description") {
        cur.description = value;

        cur.man_days = calculateManDaysByIndustry(
          cur.type_of_industry,
          cur.unit || 0,
          cur.no_of_production_line || 0,
          value
        );
      }
      else if (field === "man_days") {
        cur.man_days = value;
      }
      else if (field === "unit") {
        // unit is read-only in UI but still allow programmatic changes if needed
        cur.unit = value;
        // recalc man_days based on type
        // cur.man_days = calculateManDaysByIndustry(cur.type_of_industry, value, cur.no_of_production_line);
        cur.man_days = calculateManDaysByIndustry(
          cur.type_of_industry,
          value,
          cur.no_of_production_line,
          cur.description
        );
      }
      else if (field === "no_of_production_line") {
        cur.no_of_production_line = value;
        // cur.man_days = calculateManDaysByIndustry(cur.type_of_industry, cur.unit, value);
        cur.man_days = calculateManDaysByIndustry(
          cur.type_of_industry,
          cur.unit,
          value,
          cur.description
        );
      }

      next[index] = cur;
      return next;
    });
  };

  const handleSubmit = async () => {
    try {
      await form.validateFields();
      const formData = form.getFieldsValue();

      const cleanedOutlets = outletItem.map((it) => ({
        ...it,
        quantity: Number(it.quantity) || 0,
        unit_cost: Number(it.unit_cost) || 0,
        amount: Number(it.amount) || 0,
      }));

      const payload = {
        ...formData,
        outlets: cleanedOutlets,
        remark: formData.note || "",
        po_number: formData.po_number || "",
      };
      // console.log("Update payload:", payload);
      // update request
      const response = await axios.put(`${apiUrl}/api/proposal/updateProposalAndOutlet/${proposalId}`, payload);

      // console.log("Update response:", response.data);
      message.success("Proposal Updated successfully!");
      onOk();
      // reset modal state
      setItems([]);
      form.resetFields();
    } catch (err) {
      console.error("Update error:", err);
      message.error("Failed to update proposal.");
    }
  };

  // Table columns, with Production Line conditional
  const columnsBase = [
    // {
    //   title: "S/N",
    //   key: "sn",
    //   render: (_, __, index) => index + 1,
    //   width: 60,
    // },
    {
      title: "Outlet name",
      dataIndex: "outletId",
      width: 60,
      render: (text, record, index) => (
        <Select
          labelInValue
          value={
            record.outletId
              ? {
                value: record.outletId, label: record.outlet_name +
                  (record.city ? ` - ${record.city}` : record.outletId)
              }
              : undefined
          }
          onChange={(opt) => handleOutletSelect(index, opt)}
          className="w-full"
          style={{ width: 100 }}
          placeholder="Select outlet"
        >
          <Option value="">Select</Option>
          <Option value="others">Others</Option>
          {outlets.map((o) => (
            <Option key={o._id} value={o._id}>
              {/* {o.branch_name || o.outlet_name} */}
              {(o.branch_name || o.outlet_name) + (o.city ? ` - ${o.city}` : "")}
            </Option>
          ))}
        </Select>
      ),
    },
    {
      title: "Services",
      dataIndex: "description",
      render: (text, record, index) =>
        record.outlet_name === "Others" ? (
          <Input value={text} onChange={(e) => handleInputChange(index, "description", e.target.value)} />
        ) : (
          <Select value={text} onChange={(v) => handleInputChange(index, "description", v)} style={{ width: 90 }}>
            <Option value="TPA">TPA</Option>
            <Option value="Hygiene Rating">Hygiene Rating</Option>
            <Option value="Eat Right Station (Pre-Audit)">Eat Right Station(Pre-Audit)</Option>
            <Option value="Eat Right Station (Final-Audit)">Eat Right Station(Final-Audit)</Option>
            <Option value="Eat Right Station (One-Time)">Eat Right Station(One-Time)</Option>
            <Option value="Clean and Fresh Fruit and Vegetable Market (Pre-Audit)">
              Clean and Fresh Fruit and Vegetable Market(Pre-Audit)
            </Option>
            <Option value="Clean and Fresh Fruit and Vegetable Market (Final-Audit)">
              Clean and Fresh Fruit and Vegetable Market(Final-Audit)
            </Option>
            <Option value="Clean and Fresh Fruit and Vegetable Market (One-Time)">
              Clean and Fresh Fruit and Vegetable Market(One-Time)
            </Option>
            <Option value="Clean Street Food Hub (Pre-Audit)">Clean Street Food Hub(Pre-Audit)</Option>
            <Option value="Clean Street Food Hub (Final-Audit)">Clean Street Food Hub(Final-Audit)</Option>
            <Option value="Clean Street Food Hub (One-Time)">Clean Street Food Hub(One-Time)</Option>
            <Option value="Eat Right Campus (Pre-Audit)">Eat Right Campus(Pre-Audit)</Option>
            <Option value="Eat Right Campus (Final-Audit)">Eat Right Campus(Final-Audit)</Option>
            <Option value="Eat Right Campus (One-Time)">Eat Right Campus(One-Time)</Option>
            <Option value="BHOG (Blissful Hygienic Offering to God) (Pre-Audit)">BHOG (Blissful Hygienic Offering to God)(Pre-Audit)</Option>
            <Option value="BHOG (Blissful Hygienic Offering to God) (Final-Audit)">BHOG (Blissful Hygienic Offering to God)(Final-Audit)</Option>
            <Option value="BHOG (Blissful Hygienic Offering to God) (One-Time)">BHOG (Blissful Hygienic Offering to God)(One-Time)</Option>
          </Select>
        ),
    },
    {
      title: "Criteria",
      dataIndex: "unit",
      render: (text, record, index) => {
        const postfix =
          record.type_of_industry === "Transportation"
            ? "VH"
            : record.type_of_industry === "Catering"
              ? "FH"
              : (record.type_of_industry === "Trade and Retail" || record.type_of_industry === "Storage and Warehouse")
                ? "Sq ft"
                : record.type_of_industry === "Manufacturing"
                  ? "FH"
                  : "";

        // show incoming value read-only
        return (
          <div className="flex items-center">
            <InputNumber value={record.unit ?? 0} disabled className="w-full" />
            <span className="ml-1" style={{ width: 50 }}>
              {postfix}
            </span>
          </div>
        );
      },
    },
  ];

  // Insert Production Line column only when any row is manufacturing
  const columnsDynamic = [...columnsBase];
  if (hasManufacturing) {
    columnsDynamic.push({
      title: "Production Line",
      dataIndex: "no_of_production_line",
      render: (text, record) => (
        <div className="flex items-center">
          <InputNumber value={record.no_of_production_line ?? 0} disabled className="w-full" />
          {/* <span className="ml-2" style={{ width: 50 }}>
            PD/Line
          </span> */}
        </div>
      ),
    });
  }

  // remaining columns
  columnsDynamic.push(
    {
      title: "Man Days",
      dataIndex: "man_days",
      render: (text, record, index) => (
        <InputNumber
          min={0}
          value={record.man_days}
          onChange={(value) => handleInputChange(index, "man_days", value)}
          className="w-full"
        />
      ),
    },
    {
      title: "QTY",
      dataIndex: "quantity",
      render: (text, record, index) => (
        <InputNumber min={0} value={text} onChange={(value) => handleInputChange(index, "quantity", value)} className="w-full" />
      ),
    },
    {
      title: "Unit Cost(₹)",
      dataIndex: "unit_cost",
      render: (text, record, index) => (
        <InputNumber min={0} value={text} onChange={(value) => handleInputChange(index, "unit_cost", value)} className="w-full" />
      ),
    },
    {
      title: "Amount(₹)",
      dataIndex: "amount",
      render: (text) =>
        (Number(text) || 0).toLocaleString("en-IN", {
          style: "currency",
          currency: "INR",
        }),
    },
    {
      title: "Action",
      dataIndex: "action",
      render: (_, __, index) => (
        <Button onClick={() => removeItem(index)} type="link" danger icon={<DeleteOutlined />} />
      ),
    }
  );

  return (
    <Modal visible={visible} onCancel={() => { setItems([]); onCancel(); }} footer={null} width={1000} className="acc-modal">
      <Form layout="vertical" form={form} onFinish={handleSubmit}>
        <div className="text-center align-middle font-medium text-xl title-div bg-blue-50 p-7" style={{ boxShadow: "0 4px 2px -2px lightgrey" }}>
          Update Generate Proposal
        </div>

        <div className="px-8 pt-4 pb-8" style={{ backgroundColor: "#F6FAFB" }}>
          <div className="text-center font-medium text-xl mb-5 rounded-md">Document Preview</div>

          <div className="flex space-x-4">
            <Form.Item label="FBO name (Business Name)" name="fbo_name" className="flex-1" rules={[{ required: true, message: "Please input the business name!" }]}>
              <Input type="text" className="w-full p-2 border border-gray-300 rounded" />
            </Form.Item>

            <Form.Item label="Proposal date" name="proposal_date" className="flex-1" rules={[{ required: true, message: "Please select the start date!" }]}>
              <DatePicker className="w-full" format="DD/MM/YYYY" />
            </Form.Item>

            <Form.Item label="Proposal number" name="proposal_number" className="flex-1">
              <Input readOnly placeholder="Auto Generated" className="w-full p-2 border border-gray-300 rounded" />
            </Form.Item>
            <Form.Item
              label="PO Number"
              name="po_number"
              className="flex-1"
            >
              <Input
                placeholder="PO Number"
                className="w-full p-2 border border-gray-300 rounded"

              />
            </Form.Item>
          </div>

          <Form.Item label="Address">
            <Input.Group>
              <Form.Item name={["address", "line1"]} rules={[{ required: true, message: "Please input the address line 1!" }]}>
                <Input className="w-full p-2 border border-gray-300 rounded" />
              </Form.Item>
              <Form.Item name={["address", "line2"]}>
                <Input className="w-full p-2 border border-gray-300 rounded" />
              </Form.Item>
            </Input.Group>
          </Form.Item>

          <div className="flex space-x-4">
            <Form.Item label="Pincode" name="pincode" className="flex-1" rules={[{ required: true, message: "Please input the pincode!" }]}>
              <Input className="w-full p-2 border border-gray-300 rounded" />
            </Form.Item>
            <Form.Item label="GST Number" name="gst_number" className="flex-1">
              <Input className="w-full p-2 border border-gray-300 rounded" />
            </Form.Item>
          </div>

          <div className="flex space-x-4">
            <Form.Item label="Contact Person Name" name="contact_person" className="flex-1" rules={[{ required: true, message: "Please input the contact person name!" }]}>
              <Input className="w-full p-2 border border-gray-300 rounded" />
            </Form.Item>
            <Form.Item label="Contact Person Number" name="phone" className="flex-1" rules={[{ required: true, message: "Please input the contact person number!" }]}>
              <Input className="w-full p-2 border border-gray-300 rounded" />
            </Form.Item>
          </div>

          <Form.Item name="representative" label="Representative" rules={[{ required: true, message: "Please select an auditor!" }]}>
            <Select placeholder="Select an auditor">
              {representaive.map((r) => (
                <Option key={r._id} value={r._id}>
                  {r.userName}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="auditor_convenience_fee" label="Auditor Convenience Fee (₹)">
            <InputNumber min={0} placeholder="Enter convenience fee" className="w-full" formatter={(value) => `₹ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")} parser={(v) => v.replace(/₹\s?|(,*)/g, "")} onChange={() => calculateTotals(outletItem)} />
          </Form.Item>

          <div className="my-4">
            <h3 className="text-lg font-semibold mb-2">Items table</h3>
            <Table dataSource={outletItem} columns={columnsDynamic} pagination={false} rowKey={(_, idx) => `row-${idx}`} />
            <button type="button" onClick={addItem} className="mt-2 px-4 py-2 bg-blue-500 text-white rounded">+ Add New Row</button>
          </div>

          <div className="border-t pt-4">
            <Form.Item label="Note" name="note">
              <TextArea rows={4} placeholder="Enter any additional details" />
            </Form.Item>
          </div>

          <div className="my-4 p-4 border rounded w-1/2 ml-auto bg-gray-50">
            <div className="flex justify-between items-center">
              <div className="text-sm font-medium">Sub Total:</div>
              <div className="text-sm font-medium">{subTotal.toLocaleString("en-IN", { style: "currency", currency: "INR" })}</div>
            </div>

            {sameState ? (
              <>
                <div className="flex justify-between items-center">
                  <div className="text-sm font-medium">SGST (9%):</div>
                  <div className="text-sm font-medium">{sgst.toLocaleString("en-IN", { style: "currency", currency: "INR" })}</div>
                </div>
                <div className="flex justify-between items-center">
                  <div className="text-sm font-medium">CGST (9%):</div>
                  <div className="text-sm font-medium">{cgst.toLocaleString("en-IN", { style: "currency", currency: "INR" })}</div>
                </div>
                <div className="flex justify-between items-center">
                  <div className="text-sm font-medium">IGST :</div>
                  <div className="text-sm font-medium">{igst.toLocaleString("en-IN", { style: "currency", currency: "INR" })}</div>
                </div>
              </>
            ) : (
              // <div className="flex justify-between items-center">
              //   <div className="text-sm font-medium">IGST (18%):</div>
              //   <div className="text-sm font-medium">{igst.toLocaleString("en-IN", { style: "currency", currency: "INR" })}</div>
              // </div>
              <>
                <div className="flex justify-between items-center">
                  <div className="text-sm font-medium">IGST (18%):</div>
                  <div className="text-sm font-medium">{igst.toLocaleString("en-IN", { style: "currency", currency: "INR" })}</div>
                </div>
                <div className="flex justify-between items-center">
                  <div className="text-sm font-medium">SGST :</div>
                  <div className="text-sm font-medium">{sgst.toLocaleString("en-IN", { style: "currency", currency: "INR" })}</div>
                </div>
                <div className="flex justify-between items-center">
                  <div className="text-sm font-medium">CGST :</div>
                  <div className="text-sm font-medium">{cgst.toLocaleString("en-IN", { style: "currency", currency: "INR" })}</div>
                </div>
              </>
            )}

            <div className="flex justify-between items-center">
              <div className="text-lg font-bold">Total:</div>
              <div className="text-lg font-bold">{total.toLocaleString("en-IN", { style: "currency", currency: "INR" })}</div>
            </div>
          </div>

          <div className="text-center mt-4">
            <button className="bg-buttonModalColor px-4 py-2 text-white rounded" htmlType="submit">
              Update
            </button>
          </div>
        </div>
      </Form>
    </Modal>
  );
};

export default UpdateGenerateProposalModal;
